import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { API_ERROR_MESSAGE, NA_LABEL } from '../common/AppConstants';

export const getLocalCache = (key, defaultValue) => {
  const storedValue = localStorage.getItem(key);
  if (storedValue !== null) {
    const parsedValue = JSON.parse(storedValue);
    if (typeof defaultValue === 'number') {
      return parseInt(parsedValue, 10); // Always parse as base-10 number
    }
    if (typeof defaultValue === 'boolean') {
      return Boolean(parsedValue); // Ensure it's a boolean
    }
    return parsedValue; // Return as-is for other types (string, object, etc.)
  }
  return defaultValue;
};

export const setLocalCache = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};


export const useURLParams = () => {
  // Thread Unsafe - Might be effected by Race Condition if multiple instance of this class exist at the same time in DOM
  
  const [urlParam, setUrlParams] = useSearchParams();

  const updateUrlParam = (key, value) => {
    setUrlParams(currentParams => {
      const newParams = new URLSearchParams(currentParams);
      console.log(`URL Param Initial ${newParams} for ${key}:${value}`);
      
      if (value === null || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
      
      console.log(`URL Param Final ${newParams} for ${key}:${value}`);
      return newParams;
    }, { replace: true });
  };

  return { urlParam, updateUrlParam };
};

export const trimString = (value, max_length=256) => {
  const value_str = String(value);
  return value_str.length > max_length ? value_str.slice(0, max_length-3) + '...' : value_str;
};

export const notifyClientErrors = (errorDict, addNotification) => {
  try {
    if (typeof errorDict === "string") {
      errorDict = { message: [errorDict] };
    }
    Object.entries(errorDict || {}).forEach(([field, errors]) => {
      const fieldName = field.replaceAll('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
      // Normalize errors to array
      const errorList = Array.isArray(errors) ? errors : ( errors ? [errors] : [] );
      errorList.forEach(error => {
        addNotification(`${fieldName}: ${trimString(error)}`, 'warning');
      });
    });
  } catch (err) {
    console.error('Error in notifyClientErrors:', err);
    addNotification?.(API_ERROR_MESSAGE.error, 'error');
  }
};

export const OrgTermsAcceptedColumn = {
  attr: 'org_terms_accepted',
  editable: true,
  extra: false,
  label: 'I agree and accept these Terms & Conditions',
  related: false,
  required: true,
  type: 'boolean',
  help_text: 'Terms and Conditions accepted ?',
}

export function renderTermsWithPlaceholders(template, replacements) {
  const parts = template.split(/(\{\{.*?\}\})/g); // Split by {{placeholders}}

  return parts.map((part, index) => {
    const match = part.match(/\{\{(.*?)\}\}/);
    if (match) {
      const key = match[1];
      return <React.Fragment key={index}>{replacements[key] ?? part}</React.Fragment>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

export const titleCase = (key) => {
  if (!key) return "";
  return key
    .replace(/_/g, " ")      // replace underscores with spaces
    .replace(/\b\w/g, (c) => c.toUpperCase()); // capitalize words
};

export const formatCurrency = (val, max_decimal_digits = 2) => {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: max_decimal_digits }).format(val);
}

export const humanReadableDateTime = (naive_date_time) => {
  if (!naive_date_time) {
    return
  }
  return format(parseISO(naive_date_time), 'EEE dd MMM yyyy, h:mm a');
};

export const humanReadableDate = (naive_date, with_day=false) => {
  if (!naive_date) {
    return
  }
  return format(parseISO(naive_date), with_day ? 'EEE dd MMM yyyy' : 'dd MMM yyyy');
};

export  const formatTimeToAmPm = (time_str) => {
  if (typeof time_str === 'string' && ( /^\d{2}:\d{2}$/.test(time_str) || /^\d{2}:\d{2}:\d{2}$/.test(time_str))) {
    const today = new Date();
    const [hours, minutes] = time_str.split(':').map(Number);
    today.setHours(hours, minutes, 0, 0);
    return today.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true, // 12-hour format with AM/PM
    });
  }
  return 'Invalid';
}

export const getDefaultDateTime = (field) => {
  const today = new Date();
  let hour = 7; // 7 AM
  if (field.startsWith("end")) {
    hour = 17;  // 5 PM
  }
  today.setHours(hour, 0, 0, 0);
  return format(today, "yyyy-MM-dd'T'HH:mm");
}

export const getRelatedDataMap = (related_data) => {
  // Get id: item map from list of item
  const maps = {};
  for (const key in related_data) {
    maps[key] = {};
    for (const item of related_data[key]) {
      maps[key][item.id] = item;
    }
  }
  return maps;
};



export const FITNESS_SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  PENDING: 'pending',
  CANCELLED: 'cancelled',
};

export const FITNESS_SUBSCRIPTION_DATE_STATUS = {
  FUTURE: 'future',
  CURRENT: 'current',
  PAST: 'past',
};

export const fitnessMembershipStatusText = (
  status,
  dateStatus,
  isCurrent
) => {
  if (isCurrent) {
    return 'Active';
  }

  if (status === FITNESS_SUBSCRIPTION_STATUS.ACTIVE) {
    if (dateStatus === FITNESS_SUBSCRIPTION_DATE_STATUS.FUTURE) {
      return 'Upcoming';
    }

    if (dateStatus === FITNESS_SUBSCRIPTION_DATE_STATUS.PAST) {
      return 'Expired';
    }
  }

  if (status === FITNESS_SUBSCRIPTION_STATUS.CANCELLED) {
    return 'Cancelled';
  }

  if (status === FITNESS_SUBSCRIPTION_STATUS.PENDING) {
    return dateStatus === FITNESS_SUBSCRIPTION_DATE_STATUS.FUTURE
      ? 'Upcoming Payment'
      : 'Payment Pending';
  }

  return 'Unknown';
};

export const fitnessMembershipStatusColor = (
  status,
  dateStatus,
  isCurrent,
  asHex=true,
) => {
  if (isCurrent) {
    return asHex ? "#28B463" : 'success';
  }

  if (status === FITNESS_SUBSCRIPTION_STATUS.ACTIVE) {
    if (dateStatus === FITNESS_SUBSCRIPTION_DATE_STATUS.FUTURE) {
      return asHex ? "#2E86C1 " : 'info';
    }

    if (dateStatus === FITNESS_SUBSCRIPTION_DATE_STATUS.PAST) {
      return asHex ? "#D4AC0D" : 'warning';
    }
  }

  if (status === FITNESS_SUBSCRIPTION_STATUS.CANCELLED) {
    return asHex ? "#CA6F1E" : 'error';
  }

  if (status === FITNESS_SUBSCRIPTION_STATUS.PENDING) {
    return asHex ? "#D4AC0D" : 'warning';
  }

  return asHex ? "#555555" : 'grey';
};



export const FITNESS_PAYMENT_ITEM_TYPE_LABEL = {
  admission: 'Joining Fee',
  regular: 'Regular Fee',
  pt: 'PT Fee',
};

export const fitnessPaymentStatusColor = (status) => {
  switch (status) {
    case 'paid':    return 'success';
    case 'pending':   return 'warning';
    case 'failed':   return 'error';
    default:          return 'grey';
  }
};
